<template>
  <!-- Loading -->
  <div v-if="loading" class="notification-state">
    <div class="spinner-border txt-primary" role="status">
      <span class="visually-hidden">{{ t('alert.alerts.loading') }}</span>
    </div>
  </div>

  <!-- Error -->
  <div v-else-if="error" class="notification-state">
    <svg
      class="state-icon state-icon--error"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <p class="state-message">{{ error }}</p>
    <button class="btn btn-sm btn-outline-primary mt-2" @click="emit('retry')">
      {{ t('alert.alerts.error.retry') }}
    </button>
  </div>

  <!-- Empty state -->
  <div v-else-if="unreadCount === 0" class="notification-state">
    <svg
      class="state-icon state-icon--success"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
      <path
        d="M9 12l2 2 4-4"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <p class="state-message">{{ t('alert.alerts.noAlerts.title') }}</p>
    <small class="state-sub">{{ t('alert.alerts.noAlerts.message') }}</small>
  </div>

  <div v-else-if="alerts.length === 0 && unreadCount > 0" class="d-flex justify-content-center py-4">
    <button
    class="btn btn-sm btn-link"
    @click.stop="emit('load-more')"
  >
    {{ t('alert.alerts.noAlerts.loadMore', { count: unreadCount }) }}
  </button>
  </div>


  <!-- Alert list -->
  <div v-else class="notification-list">
    <div
      v-for="alert in processedAlerts"
      :key="alert.id"
      class="alert-card"
      :class="[alert.priorityClass, { muting: alert.isMuting }]"
    >
      <div class="alert-header">
        <div class="alert-title">{{ alert.title || alert.message }}</div>
      </div>

      <div class="alert-description">
        {{ alert.description || alert.message }}
      </div>

      <!-- Spending progress bar (only for SPENDING_LIMIT alerts) -->
      <div v-if="alert.progress" class="alert-progress">
        <div class="progress-bar">
          <div
            class="progress-fill"
            :class="{ 'over-limit': alert.progress.isOverLimit }"
            :style="{ width: Math.min(alert.progress.percentage, 100) + '%' }"
          ></div>
        </div>
        <div class="progress-text">
          {{ alert.progress.current }} {{ alert.progress.currency }} of {{ alert.progress.limit }}
          {{ alert.progress.currency }} weekly limit ({{ Math.round(alert.progress.percentage) }}%)
        </div>
      </div>

      <div v-if="alert.type === 'FLEET_EXPORT_READY' && alert.metadata?.exportCenterPath" class="alert-export-link">
        <router-link
          :to="alert.metadata.exportCenterPath"
          class="alert-action-btn txt-primary"
          @click="emit('navigate')"
        >
          <i class="fa fa-external-link me-1"></i>{{ t('alert.alerts.metadata.viewInExportCenter') }}
        </router-link>
      </div>

      <div class="alert-footer">
        <div class="alert-timestamp">{{ formatTimestamp(alert.createdAt) }}</div>
        <button class="alert-action-btn txt-primary" @click="markAlertAsRead(alert.id)">
          {{ t('alert.alerts.markAsRead') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMarkAlertAsRead } from '@/composables/alerts/useMarkAlertAsRead';

defineOptions({ name: 'AlertList' });

const props = defineProps({
  alerts: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  error: { type: String, default: null },
  unreadCount: { type: Number, default: 0 },
});

const emit = defineEmits(['retry', 'load-more', 'navigate']);

const { t } = useI18n();
const { markingAsReadIds, markAlertAsRead } = useMarkAlertAsRead();

const processedAlerts = computed(() =>
  props.alerts.map((alert) => ({
    ...alert,
    progress: getAlertProgress(alert),
    description: alert.description || alert.message,
    priorityClass: getPriorityClass(alert.priority),
    isMuting: markingAsReadIds.value.includes(alert.id),
  }))
);


function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const diffMinutes = Math.floor((Date.now() - date) / 60000);

  if (diffMinutes < 1) return t('alert.alerts.timeAgo.justNow');
  if (diffMinutes < 60) return t('alert.alerts.timeAgo.minutesAgo', { count: diffMinutes });
  if (diffMinutes < 1440)
    return t('alert.alerts.timeAgo.hoursAgo', { count: Math.floor(diffMinutes / 60) });
  return t('alert.alerts.timeAgo.daysAgo', { count: Math.floor(diffMinutes / 1440) });
}

function getAlertProgress(alert) {
  if (alert.type === 'SPENDING_LIMIT' && alert.spendingData) {
    const { currentSpending, limit, currency } = alert.spendingData;
    const percentage = (currentSpending / limit) * 100;
    return { current: currentSpending, limit, currency, percentage, isOverLimit: percentage > 100 };
  }
  return null;
}

function getPriorityClass(priority) {
  const map = { URGENT: 'urgent', WARNING: 'warning', INFO: 'info' };
  return map[priority] || '';
}
</script>

<style scoped>
/* Shared state wrapper (loading / error / empty) */
.notification-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
}

.state-icon {
  display: block;
  margin-bottom: 16px;
}

.state-icon--success {
  width: 64px;
  height: 64px;
  color: var(--bs-success);
}

.state-icon--error {
  width: 48px;
  height: 48px;
  color: var(--bs-danger);
}

.state-message {
  margin: 0;
  font-size: 14px;
  color: var(--chart-text-color);
}

.state-sub {
  font-size: 12px;
  color: var(--chart-text-color);
}

/* Alert list */
.notification-list {
  max-height: 75vh;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  min-width: 0;
  padding: 16px;
}

.notification-list::-webkit-scrollbar {
  width: 6px;
}
.notification-list::-webkit-scrollbar-track {
  background: var(--light-bg);
  border-radius: 3px;
}
.notification-list::-webkit-scrollbar-thumb {
  background: rgba(var(--bs-primary-rgb), 0.25);
  border-radius: 3px;
}
.notification-list::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--bs-primary-rgb), 0.4);
}

/* Alert Card */
.alert-card {
  background: var(--white);
  border-radius: 8px;
  border-left: 4px solid var(--bs-danger);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  padding: 20px;
  transition: all 0.2s ease;
}

.alert-card:last-child {
  margin-bottom: 0;
}


.alert-card.muting {
  opacity: 0.4;
  pointer-events: none;
}

.alert-card.urgent {
  border-left-color: var(--bs-danger);
  background: linear-gradient(135deg, rgba(var(--bs-danger-rgb), 0.05) 0%, var(--white) 100%);
}

.alert-card.warning {
  border-left-color: var(--bs-warning);
  background: linear-gradient(135deg, rgba(var(--bs-warning-rgb), 0.05) 0%, var(--white) 100%);
}

.alert-card.info {
  border-left-color: var(--bs-info);
  background: linear-gradient(135deg, rgba(var(--bs-info-rgb), 0.05) 0%, var(--white) 100%);
}

.alert-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.alert-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  color: var(--body-font-color);
  flex: 1;
  margin-right: 12px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.alert-description {
  font-size: 13px;
  line-height: 1.5;
  color: var(--chart-text-color);
  margin-bottom: 16px;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Progress Bar */
.alert-progress {
  margin-bottom: 16px;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background-color: var(--chart-border);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background-color: var(--bs-success);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-fill.over-limit {
  background-color: var(--bs-danger);
}

.progress-text {
  font-size: 12px;
  color: var(--chart-text-color);
  text-align: center;
}

/* Export Center link row */
.alert-export-link {
  margin-bottom: 10px;
}

/* Alert Footer */
.alert-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--chart-border);
}

.alert-timestamp {
  font-size: 12px;
  color: var(--chart-text-color);
}

.alert-action-btn {
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.alert-action-btn:hover {
  /* background-color: #E0DDFF; */
  text-decoration: underline;
}

@media (max-width: 768px) {
  .notification-list {
    max-height: 60vh;
    padding: 12px;
  }
  .alert-card {
    padding: 16px;
    margin-bottom: 12px;
  }
  .alert-title {
    font-size: 13px;
  }
  .alert-description {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .notification-list {
    max-height: 55vh;
    padding: 8px;
  }
  .alert-card {
    padding: 12px;
    margin-bottom: 8px;
  }
  .alert-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
